/**
 * Base Service — pure error-message helpers
 *
 * Extracted from base.service.ts to keep the abstract class within the
 * per-file code-size budget. These are stateless, side-effect-free string
 * extractors used only by BaseService.handleDatabaseError. They deliberately
 * take a plain `{ message?: string }` shape so they stay decoupled from the
 * Supabase/Postgres error types.
 */

/**
 * Extract duplicate field name from error message
 */
export function extractDuplicateFieldFromError(error: {
  message?: string;
  details?: string | null;
}): string {
  // PostgREST splits a 23505 across two fields: `message` carries
  // 'violates unique constraint "<name>"' and `details` carries
  // 'Key (<column>)=(...) already exists.'. Read both — naming the wrong
  // column sends the user chasing an innocent field (BAL-844).
  const message = error.message || '';
  const details = error.details || '';

  const keyMatch = `${details} ${message}`.match(/Key \((.*?)\)=/);

  if (keyMatch && keyMatch[1]) {
    return `Duplicate value for field: ${keyMatch[1]}`;
  }

  const constraintMatch = message.match(/violates unique constraint "(.*?)"/);

  if (constraintMatch && constraintMatch[1]) {
    return `Duplicate value violates constraint: ${constraintMatch[1]}`;
  }

  return 'Duplicate entry exists';
}

/**
 * Readable text for a check violation (23514). A CHECK constraint names
 * itself ("violates check constraint ..."); a trigger raising 23514 writes
 * its own message for the reader, so that message is kept as is. Before
 * this, a trigger refusal such as the invoice issuer's missing bank account
 * reached the admin as "Validation constraint violated".
 */
export function extractCheckConstraintFromError(error: {
  message?: string;
}): string {
  const message = error.message || '';
  const constraintMatch = message.match(/violates check constraint "(.*)"/);

  if (constraintMatch && constraintMatch[1]) {
    // Convert snake_case constraint name to readable message
    const constraintName = constraintMatch[1]
      .replace(/_/g, ' ')
      .replace(/check$/, '')
      .trim();

    return `Validation failed: ${constraintName}`;
  }

  return message.trim() || 'Validation constraint violated';
}
